<template>
  <div id="agent-invites-management" class="fill-height align-start">
    <i-overlay
      v-if="fetching && $vuetifyDisplay.smAndDown"
      text="Loading Agent Invites data..."
    />

    <v-row v-else class="g-container" no-gutters>
      <v-dialog
        v-if="ruleCreationDialog"
        v-model="ruleCreationDialog"
        max-width="750px"
        persistent
        @click:outside="cancelRuleCreation"
        @keydown.esc="cancelRuleCreation"
      >
        <i-create-agent-invite
          @cancel="cancelRuleCreation"
          @confirm="confirmRuleCreation"
        />
      </v-dialog>

      <v-dialog
        v-if="dialogConfirmation"
        v-model="dialogConfirmation"
        width="400px"
        @click:outside="cancelConfirmationDialog"
        @keydown.esc="cancelConfirmationDialog"
      >
        <i-confirmation-dialog
          :text="dialogText"
          :action-text="dialogActionText"
          :submitting="updating"
          @cancel="cancelConfirmationDialog"
          @confirm="confirmRuleDeletion"
        />
      </v-dialog>

      <v-dialog
        v-if="ruleEditionDialog"
        v-model="ruleEditionDialog"
        max-width="750px"
        persistent
        @click:outside="cancelRuleEdition"
        @keydown.esc="cancelRuleEdition"
      >
        <i-edit-agent-invite
          :rule="selectedRule"
          @cancel="cancelRuleEdition"
          @confirm="confirmRuleEdition"
        />
      </v-dialog>

      <v-dialog
        v-if="dialogConfirmation"
        v-model="dialogConfirmation"
        width="400px"
        @click:outside="cancelConfirmationDialog"
        @keydown.esc="cancelConfirmationDialog"
      >
        <i-confirmation-dialog
          :text="dialogText"
          :action-text="dialogActionText"
          :submitting="updating"
          @cancel="cancelConfirmationDialog"
          @confirm="confirmRuleDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0 mb-4">{{ $route.meta.title }}</h2>

        <v-row>
          <template v-if="userIsAllowedToEdit">
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="ruleCreationDialog = true"
              >
                Invite Agent
              </v-btn>
            </v-col>
          </template>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="reports/purchase-reports/purchase-metrics"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <i-local-filter
              :filterPlaceholder="'Search by Email, First or Last Name'"
              @filter="localFilter"
            />
          </v-col>
        </v-row>

        <template v-if="!fetching && filteredAgentRules.length === 0">
          <i-empty-results
            :buttonText="`Turn In Orders for ${isILR ? 'Customer' : 'Retailer'}`"
            buttonRouteName="retailer-turn-in-orders"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <template v-if="$vuetifyDisplay.smAndDown">
            <v-col
              class="mt-0 pa-0"
              v-for="(item, index) in filteredAgentRules"
              :key="index"
            >
              <v-card class="rounded-2 pa-4 mb-6">
                <v-row no-gutters>
                  <v-col class="py-1 g-bold">
                    <span>{{ headers[0].text }}</span>
                    <br />
                    <span>{{ item.agentInfo.email }}</span>
                  </v-col>
                </v-row>

                <v-divider class="my-2"></v-divider>

                <v-row no-gutters>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[1].text }}</span>
                  </v-col>
                  <v-col cols="6" class="pl-4">
                    <span>{{ item.agentFirstName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[2].text }}</span>
                  </v-col>
                  <v-col cols="6" class="pl-4">
                    <span>{{ item.agentLastName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[3].text }}</span>
                  </v-col>
                  <v-col cols="6" class="pl-4">
                    <span>{{ item.agentMobile }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[4].text }}</span>
                  </v-col>
                  <v-col cols="6" class="pl-4">
                    <v-chip
                      v-if="item.agentInfo.status === AGENT_STATUSES.EXISTS"
                      x-small
                      label
                      class="i-tag-success c-mini"
                      >REGISTERED</v-chip
                    >
                    <v-chip
                      v-if="item.agentInfo.status === AGENT_STATUSES.NOT_EXISTS"
                      x-small
                      label
                      class="i-tag-error c-mini"
                      >NOT REGISTERED</v-chip
                    >
                    <v-chip
                      v-if="
                        item.agentInfo.status ===
                        AGENT_STATUSES.INVITATION_PENDING
                      "
                      x-small
                      label
                      class="i-tag-warning c-mini"
                      >INVITED</v-chip
                    >
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>{{ headers[5].text }}</span>
                  </v-col>
                  <v-col cols="6" class="pl-4">
                    <v-chip
                      class="mr-1 my-1"
                      color="primary"
                      outlined
                      x-small
                      v-for="(
                        state, index
                      ) in item.targetStoreDeliveryAddressStates"
                      :key="index"
                    >
                      <span>{{ state.state }}</span>
                    </v-chip>
                  </v-col>
                </v-row>

                <template v-if="userIsAllowedToEdit">
                  <v-divider class="my-3"></v-divider>
                  <v-row no-gutters>
                    <v-col cols="12" class="py-1">
                      <v-btn
                        class="i-btn-actions-blue g-full-width"
                        :disabled="fetching || updating"
                        @click="handleEditRule(item)"
                      >
                        <span>Edit rule</span>
                        <v-icon small class="ml-1"
                          >mdi-circle-edit-outline</v-icon
                        >
                      </v-btn>
                    </v-col>
                    <v-col cols="12" class="py-1">
                      <v-btn
                        class="i-btn-actions-red g-full-width"
                        :disabled="fetching || updating"
                        @click="handleDeleteRule(item)"
                      >
                        <span>Delete rule</span>
                        <v-icon small class="ml-1">mdi-delete-outline</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
              </v-card>
            </v-col>
          </template>

          <v-data-table
            v-else
            class="pt-2 elevation-1 i-data-table"
            hide-default-footer
            item-key="email"
            :headers="headers"
            :height="tableHeight"
            :items="filteredAgentRules"
            :items-per-page="-1"
            :loading="fetching || updating"
          >
            <template #[`item.agentInfo.email`]="{ item }">
              <span>{{ item.agentInfo.email }}</span>
            </template>

            <template #[`item.agentFirstName`]="{ item }">
              <span>{{ item.agentFirstName }}</span>
            </template>

            <template #[`item.agentLastName`]="{ item }">
              <span>{{ item.agentLastName }}</span>
            </template>

            <template #[`item.agentMobile`]="{ item }">
              <span>{{ item.agentMobile }}</span>
            </template>

            <template #[`item.agentInfo.status`]="{ item }">
              <v-chip
                v-if="item.agentInfo.status === AGENT_STATUSES.EXISTS"
                small
                label
                class="i-tag-success c-mini"
                >REGISTERED</v-chip
              >
              <v-chip
                v-if="item.agentInfo.status === AGENT_STATUSES.NOT_EXISTS"
                small
                label
                class="i-tag-error c-mini"
                >NOT REGISTERED</v-chip
              >
              <v-chip
                v-if="
                  item.agentInfo.status === AGENT_STATUSES.INVITATION_PENDING
                "
                small
                label
                class="i-tag-warning c-mini"
                >INVITED</v-chip
              >
            </template>

            <template #[`item.states`]="{ item }">
              <v-chip
                class="mx-1 my-1"
                color="primary"
                outlined
                small
                v-for="(state, index) in item.targetStoreDeliveryAddressStates"
                :key="index"
              >
                <span>{{ state.state }}</span>
              </v-chip>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn
                icon
                color="secondary"
                class="i-icon-blue mx-1"
                :disabled="!userIsAllowedToEdit || fetching || updating"
                v-tooltip="{
                  content: 'Edit Agent Rule',
                  visible: $vuetifyDisplay.mdAndDown ? false : undefined
                }"
                @click="handleEditRule(item)"
              >
                <v-icon>mdi-circle-edit-outline</v-icon>
              </v-btn>

              <v-btn
                icon
                color="error"
                class="i-icon-red mx-1"
                :disabled="!userIsAllowedToEdit || fetching || updating"
                v-tooltip="{
                  content: 'Delete Agent Rule',
                  visible: $vuetifyDisplay.mdAndDown ? false : undefined
                }"
                @click="handleDeleteRule(item)"
              >
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchAgentSupplierRules,
  deleteAgentSupplierRule
} from '@/services/agencyRules'
import DataTables from '@/utils/DataTables'
import iEmptyResults from '@/components/EmptyResults'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iCreateAgentInvite from '../components/CreateAgentInvite'
import iEditAgentInvite from '../components/EditAgentInvite'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'

const AGENT_STATUSES = {
  EXISTS: 'EXISTS',
  NOT_EXISTS: 'NOT_EXISTS',
  INVITATION_PENDING: 'INVITATION_PENDING'
}

const RULES_ACTIONS = {
  EDIT: 'EDIT',
  DELETE: 'DELETE'
}

const TIMEOUT_DURATION = 1000
const VISIBLE_TABLE_ROWS = 10

export default {
  name: 'retailer-turn-in-orders',

  components: {
    iInstructions,
    iConfirmationDialog,
    iCreateAgentInvite,
    iEditAgentInvite,
    iEmptyResults,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    fetching: false,
    updating: false,
    agentRules: [],
    headers: [
      {
        text: 'EMAIL',
        value: 'agentInfo.email',
        width: '19%'
      },
      {
        text: 'FIRST NAME',
        value: 'agentFirstName',
        width: '9%'
      },
      {
        text: 'LAST NAME',
        value: 'agentLastName',
        width: '9%'
      },
      {
        align: 'center',
        text: 'CONTACT NUMBER',
        value: 'agentMobile',
        width: '13%'
      },
      {
        align: 'center',
        text: 'AGENT STATUS',
        value: 'agentInfo.status',
        width: '12%'
      },
      {
        text: 'STATE(S)',
        value: 'states',
        width: '28%',
        sortable: false
      },
      {
        align: 'center',
        text: 'ACTIONS',
        value: 'actions',
        width: '10%',
        sortable: false
      }
    ],
    emptyFilteredResultsText: 'No Agent Invites found',
    filterText: '',
    ruleCreationDialog: false,
    ruleEditionDialog: false,
    dialogAction: null,
    dialogActionText: '',
    dialogConfirmation: false,
    dialogText: '',
    selectedRule: null,
    tableHeight: 'auto',
    tableTimeout: null,
    AGENT_STATUSES
  }),

  computed: {
    ...mapGetters('preferences', ['isOwnerUser', 'isAdminUser']),
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['isILR']),

    filteredAgentRules() {
      return this.mappedAgentRules.filter((rule) => {
        const filterText = this.filterText.toLowerCase()
        return (
          rule.agentInfo.email.toLowerCase().includes(filterText) ||
          rule.agentFirstName.toLowerCase().includes(filterText) ||
          rule.agentLastName.toLowerCase().includes(filterText)
        )
      })
    },

    mappedAgentRules() {
      // Created to allow UI to render even if agent is missing details
      return this.agentRules.map((agent) => {
        return {
          ...agent,
          agentFirstName: agent.agentInfo?.userInfo?.firstName || '-',
          agentLastName: agent.agentInfo?.userInfo?.lastName || '-',
          agentMobile: agent.agentInfo?.userInfo?.mobile || '-'
        }
      })
    },

    isOverMaximumVisibleRows() {
      return this.filteredAgentRules.length > VISIBLE_TABLE_ROWS
    },

    userIsAllowedToEdit() {
      return this.isOwnerUser || this.isAdminUser
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    setTableSize() {
      if (!this.isOverMaximumVisibleRows) {
        this.tableHeight = 'auto'
        return
      }

      // Timeout needed to let UI fully render before calculating values
      clearTimeout(this.tableTimeout)
      this.tableTimeout = setTimeout(() => {
        this.tableHeight = DataTables.calculateTableHeight(VISIBLE_TABLE_ROWS)
      }, TIMEOUT_DURATION)
    },

    cancelRuleCreation() {
      this.ruleCreationDialog = false
    },

    cancelRuleEdition() {
      this.ruleEditionDialog = false
      this.selectedRule = null
    },

    cancelConfirmationDialog() {
      this.dialogConfirmation = false
      this.selectedRule = null
      this.dialogAction = null
      this.dialogText = ''
      this.dialogActionText = ''
    },

    confirmRuleCreation() {
      this.ruleCreationDialog = false
      this.fetchAgentSupplierRules()
    },

    confirmRuleEdition() {
      this.ruleEditionDialog = false
      this.selectedProduct = null
      this.fetchAgentSupplierRules()
    },

    handleDeleteRule(item) {
      this.selectedRule = item
      this.dialogAction = RULES_ACTIONS.DELETE
      this.dialogText = 'Do you want to delete this Agent Rule?'
      this.dialogActionText = 'Delete'
      this.dialogConfirmation = true
    },

    handleEditRule(item) {
      this.selectedRule = item
      this.ruleEditionDialog = true
    },

    // ***
    // Related to connecting with APIs
    // ***

    fetchAgentSupplierRules() {
      this.fetching = true

      return fetchAgentSupplierRules(this.selectedSupplierId)
        .then((response) => {
          this.agentRules = response
        })
        .catch((error) => {
          console.log(error)
          this.addNotification({
            color: 'error',
            text: 'Agent Invites could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.fetching = false
        })
    },

    confirmRuleDeletion() {
      this.updating = true

      return deleteAgentSupplierRule({
        supplierId: this.selectedSupplierId,
        agentSupplierRuleId: this.selectedRule.id
      })
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Agent Invite successfully deleted.'
          })
          this.fetchAgentSupplierRules()
        })
        .catch((error) => {
          console.log(error)
          this.addNotification({
            color: 'error',
            text: 'Agent Invite could not be deleted, please try again...'
          })
        })
        .finally(() => {
          this.updating = false
          this.cancelConfirmationDialog()
        })
    }
  },

  async mounted() {
    await this.fetchAgentSupplierRules()
    this.setTableSize()
  },

  created() {
    window.addEventListener('resize', this.setTableSize)
  },

  unmounted() {
    window.removeEventListener('resize', this.setTableSize)
  },

  watch: {
    'filteredAgentRules.length'() {
      this.tableHeight = 'auto'
      this.setTableSize()
    }
  }
}
</script>

<style lang="scss" scoped></style>
